import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface SeoProps {
  title: string;
  description: string;
}

export const SEO: React.FC<SeoProps> = ({ title, description }) => {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }
  }, [title, description]);
  return null;
};

export const Hero: React.FC<{ title: string; subtitle: string; label?: string }> = ({ title, subtitle, label }) => (
  <div className="mb-20">
    {label && <span className="text-teal-300 font-bold tracking-widest uppercase text-xs mb-4 block">{label}</span>}
    <h1 className="text-5xl md:text-7xl font-black text-white mt-4 mb-8 tracking-tighter leading-none">
      <span className="aurora-text">{title}</span>
    </h1>
    <p className="text-xl text-slate-400 leading-relaxed max-w-3xl border-l-2 border-teal-400/30 pl-6 font-medium">
      {subtitle}
    </p>
  </div>
);

export const ActionBox: React.FC<{ 
  actions?: { today: string[], week: string[], month: string[] } 
}> = ({ actions }) => {
  if (!actions) return null;
  return (
    <div className="arctic-panel p-10 rounded-[2.5rem] border border-teal-400/20 mb-12 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 p-4 opacity-10">
        <svg className="w-24 h-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="text-teal-300 font-black mb-8 text-[10px] uppercase tracking-[0.25em]">Pikatoimet (Action Plan)</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <h4 className="text-white font-black text-xs uppercase mb-4 tracking-wider flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-teal-400 rounded-full"></span> Tänään
          </h4>
          <ul className="space-y-3">
            {actions.today.map((a, i) => (
              <li key={i} className="text-sm text-slate-400 leading-snug">{a}</li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-black text-xs uppercase mb-4 tracking-wider flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-sky-400 rounded-full"></span> Tällä viikolla
          </h4>
          <ul className="space-y-3">
            {actions.week.map((a, i) => (
              <li key={i} className="text-sm text-slate-400 leading-snug">{a}</li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-black text-xs uppercase mb-4 tracking-wider flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span> Tässä kuussa
          </h4>
          <ul className="space-y-3">
            {actions.month.map((a, i) => (
              <li key={i} className="text-sm text-slate-400 leading-snug">{a}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export const Section: React.FC<{ 
  title: string; 
  children: React.ReactNode; 
  importanceTitle?: string;
  importanceDesc?: string;
  checklist?: string[];
  colorClass?: string;
  slug?: string;
  pitfalls?: string[];
}> = ({ title, children, importanceTitle, importanceDesc, checklist, colorClass = "bg-emerald-500", slug, pitfalls }) => {
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (slug) {
      const storageKey = `lail-checklist-${slug}-${title.replace(/\s+/g, '-').toLowerCase()}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          setCheckedItems(JSON.parse(saved));
        } catch (e) {
          console.error("Error parsing checklist storage", e);
        }
      }
    }
  }, [slug, title]);

  const toggleItem = (idx: number) => {
    const newItems = { ...checkedItems, [idx]: !checkedItems[idx] };
    setCheckedItems(newItems);
    if (slug) {
      const storageKey = `lail-checklist-${slug}-${title.replace(/\s+/g, '-').toLowerCase()}`;
      localStorage.setItem(storageKey, JSON.stringify(newItems));
    }
  };

  const isComplete = checklist && Object.values(checkedItems).filter(Boolean).length === checklist.length;

  return (
    <section className="mb-24 relative pl-8">
      <div className="absolute left-0 top-0 bottom-0 aurora-vertical-line rounded-full opacity-30"></div>
      
      <h2 className="text-3xl font-black text-white mb-8 flex items-center tracking-tight">
        <span className={`w-1 h-10 ${colorClass} rounded-full mr-6 shadow-lg shadow-current/50`}></span>
        {title}
        {isComplete && <span className="ml-4 text-[10px] bg-teal-400 text-slate-950 px-3 py-1 rounded-full uppercase font-black tracking-widest">Auditoitu</span>}
      </h2>
      <div className="arctic-panel p-10 rounded-[2.5rem] border border-slate-800/50 mb-8 shadow-2xl relative overflow-hidden">
        <div className="text-slate-300 leading-relaxed mb-10 text-lg relative z-10 font-medium">
          {children}
        </div>
        
        {importanceTitle && importanceDesc && (
          <div className="bg-purple-900/10 border border-purple-400/20 p-8 rounded-3xl mb-10 relative z-10">
            <h3 className="text-purple-300 font-bold mb-4 text-[10px] uppercase tracking-widest">{importanceTitle}</h3>
            <p className="text-slate-300 italic">"{importanceDesc}"</p>
          </div>
        )}

        {checklist && (
          <div className="bg-slate-900/30 p-8 rounded-3xl border border-white/5 relative z-10 mb-8">
            <h4 className="text-teal-300 font-black mb-8 flex items-center text-[10px] uppercase tracking-[0.2em]">
              Arctic Checklist
            </h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {checklist.map((item, idx) => (
                <li 
                  key={idx} 
                  onClick={() => toggleItem(idx)}
                  className={`flex items-start p-4 rounded-xl border transition-all cursor-pointer ${
                    checkedItems[idx] 
                      ? 'bg-teal-500/5 border-teal-400/20 opacity-50' 
                      : 'bg-slate-800/20 border-slate-700/30 hover:border-teal-400/20'
                  }`}
                >
                  <div className={`w-5 h-5 mr-4 shrink-0 mt-0.5 rounded flex items-center justify-center border ${
                    checkedItems[idx] ? 'bg-teal-400 border-teal-400' : 'border-slate-700'
                  }`}>
                    {checkedItems[idx] && <svg className="w-3.5 h-3.5 text-slate-950" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                  </div>
                  <span className={`text-sm leading-tight transition-all ${checkedItems[idx] ? 'text-slate-500 line-through' : 'text-slate-300 font-bold'}`}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {pitfalls && (
          <div className="bg-red-950/10 border border-red-500/20 p-8 rounded-3xl relative z-10">
            <h4 className="text-red-400 font-black mb-6 text-[10px] uppercase tracking-[0.25em]">Vältä nämä virheet</h4>
            <ul className="space-y-3">
              {pitfalls.map((p, i) => (
                <li key={i} className="text-sm text-slate-400 flex items-center gap-3">
                  <span className="text-red-500">✕</span> {p}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export const RiskCard: React.FC<{ title: string; example: string; description: string }> = ({ title, example, description }) => (
  <div className="bg-slate-900/60 border border-red-500/10 p-10 rounded-[2.5rem] h-full flex flex-col shadow-inner">
    <h4 className="text-red-400 font-black mb-6 text-xl tracking-tight uppercase">{title}</h4>
    <div className="bg-black/40 p-6 rounded-2xl mb-8 font-mono text-xs text-red-300/60 italic border border-red-500/5 leading-relaxed">"Esimerkki riskistä: {example}"</div>
    <p className="text-slate-400 text-sm leading-relaxed mt-auto font-medium">{description}</p>
  </div>
);

export const CTA: React.FC<{ label: string; onClick?: () => void; link?: string }> = ({ label, onClick, link }) => (
  <section className="text-center py-12">
    {link ? (
      <Link to={link} className="btn-primary font-black px-12 py-6 rounded-3xl transition-all text-xl inline-block transform hover:scale-105 uppercase tracking-tighter">{label}</Link>
    ) : (
      <button onClick={onClick} className="btn-primary font-black px-12 py-6 rounded-3xl transition-all text-xl transform hover:scale-105 uppercase tracking-tighter">{label}</button>
    )}
  </section>
);
